import { useState, type FormEvent } from 'react';
import {
  Button,
  ConfirmDialog,
  Input,
  Modal,
  PageHeader,
  RowMenu,
  StatusBadge,
  Switch,
} from '../../components';
import { ApiError } from '../../lib/api';
import { useCrud } from '../../lib/useCrud';

type LeadStatus = {
  id: string;
  slug: string;
  name: string;
  color: string | null;
  sort_order: number;
  is_active: boolean;
};

type FormState = {
  slug: string;
  name: string;
  color: string;
  sort_order: string;
  is_active: boolean;
};

function toFormState(row?: LeadStatus): FormState {
  return {
    slug: row?.slug ?? '',
    name: row?.name ?? '',
    color: row?.color ?? '#3b82f6',
    sort_order: String(row?.sort_order ?? 0),
    is_active: row?.is_active ?? true,
  };
}

function toBody(form: FormState) {
  return {
    slug: form.slug.trim(),
    name: form.name.trim(),
    color: form.color.trim() || null,
    sort_order: Number(form.sort_order) || 0,
    is_active: form.is_active,
  };
}

export function LeadStatusesPage() {
  const crud = useCrud<
    LeadStatus,
    ReturnType<typeof toBody>,
    Partial<ReturnType<typeof toBody>>
  >({
    key: 'lead-statuses',
    path: '/settings/lead-statuses',
    listQuery: 'include_inactive=true&limit=100',
  });

  const [editing, setEditing] = useState<LeadStatus | 'new' | null>(null);
  const [deleting, setDeleting] = useState<LeadStatus | null>(null);

  return (
    <>
      <PageHeader
        crumb="Settings"
        title="Lead statuses"
        subtitle="Pipeline stages, editable master data."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New status
          </Button>
        }
      />

      {crud.list.isLoading && <div className="card card-body muted">Loading…</div>}
      {crud.list.error && (
        <div className="alert">
          {crud.list.error instanceof Error ? crud.list.error.message : 'Failed to load'}
        </div>
      )}
      {crud.list.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Color</th>
                <th>Sort</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {crud.list.data.data.map((row, i) => (
                <tr key={row.id}>
                  <td className="col-num">{i + 1}</td>
                  <td>{row.name}</td>
                  <td className="cell-mono">{row.slug}</td>
                  <td>
                    {row.color ? (
                      <ColorSwatch color={row.color} />
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="tabular">{row.sort_order}</td>
                  <td>
                    <StatusBadge active={row.is_active} />
                  </td>
                  <td>
                    <RowMenu
                      items={[
                        { icon: 'edit', label: 'Edit', onClick: () => setEditing(row) },
                        'divider',
                        {
                          icon: 'trash',
                          label: 'Delete',
                          onClick: () => setDeleting(row),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditLeadStatusModal
          row={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            if (editing === 'new') {
              await crud.create.mutateAsync(body);
            } else {
              await crud.update.mutateAsync({ id: editing.id, body });
            }
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete lead status"
        message={
          deleting ? (
            <>
              Delete <strong>{deleting.name}</strong>? This cannot be undone.
            </>
          ) : null
        }
        destructive
        confirmLabel="Delete"
        loading={crud.remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await crud.remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function EditLeadStatusModal({
  row,
  onClose,
  onSubmit,
}: {
  row?: LeadStatus;
  onClose: () => void;
  onSubmit: (body: ReturnType<typeof toBody>) => Promise<void>;
}) {
  const [form, setForm] = useState<FormState>(() => toFormState(row));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(toBody(form));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit lead status' : 'New lead status'} width={480}>
      <form onSubmit={handleSubmit}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            id="ls-name"
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            autoFocus
          />
          <Input
            id="ls-slug"
            label="Slug"
            value={form.slug}
            onChange={(v) => setForm({ ...form, slug: v })}
            required
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label" htmlFor="ls-color">
                Color
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="ls-color"
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  style={{
                    width: 44,
                    height: 36,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 'var(--radius)',
                    background: 'var(--surface)',
                    padding: 2,
                  }}
                />
                <Input
                  value={form.color}
                  onChange={(v) => setForm({ ...form, color: v })}
                  placeholder="#RRGGBB"
                />
              </div>
            </div>
            <div style={{ width: 120 }}>
              <Input
                id="ls-sort"
                label="Sort"
                type="number"
                value={form.sort_order}
                onChange={(v) => setForm({ ...form, sort_order: v })}
              />
            </div>
          </div>
          <div className="row">
            <Switch
              on={form.is_active}
              onChange={(v) => setForm({ ...form, is_active: v })}
              label="Active"
            />
            <span className="muted">Active</span>
          </div>
          {error && <div className="alert">{error}</div>}
        </div>
        <div className="card-header" style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}>
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ColorSwatch({ color }: { color: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 14,
          height: 14,
          borderRadius: 4,
          border: '1px solid var(--border)',
          background: color,
        }}
      />
      <span className="cell-mono">{color}</span>
    </span>
  );
}

